import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Modal, Form, Spin, Select, Row, Col, Tabs } from 'antd';
import { FaRegTrashAlt, FaEdit, FaUndo } from 'react-icons/fa';
import { PlusOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const { Option } = Select;
const { TabPane } = Tabs;

const Cliente = () => {
  const [clientesAtivos, setClientesAtivos] = useState([]);
  const [clientesInativos, setClientesInativos] = useState([]);
  const [nacionalidades, setNacionalidades] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  useEffect(() => {
    document.title = 'Clientes';
    fetchClientesAtivos();
    fetchClientesInativos();
    fetchNacionalidades();
  }, []);

  const sms_error = (msg) => toast.error(msg);
  const sms_success = (msg) => toast.success(msg);

  const fetchClientesAtivos = async () => {
    setLoading(true);
    try {
      const response = await Api.ListCliente();
      const dados = Array.isArray(response) ? response : [];
      setClientesAtivos(dados.map(item => ({
        key: item.id,
        ...item
      })));
    } catch (error) {
      sms_error('Erro ao buscar clientes ativos');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientesInativos = async () => {
    try {
      const response = await Api.ListClienteInativo();
      const dados = Array.isArray(response) ? response : [];
      setClientesInativos(dados.map(item => ({
        key: item.id,
        ...item
      })));
    } catch (error) {
      sms_error('Erro ao buscar clientes inativos');
    }
  };

  const fetchNacionalidades = async () => {
    try {
      const response = await Api.ListNacionalidade();
      setNacionalidades(Array.isArray(response) ? response : []);
    } catch {
      sms_error('Erro ao buscar nacionalidades');
    }
  };

  const handleAdd = async (values) => {
    setAddLoading(true);
    try {
      const result = await Api.AddCliente(values);
      if (result.success) {
        sms_success('Cliente adicionado com sucesso!');
        setIsAddModalVisible(false);
        form.resetFields();
        await fetchClientesAtivos();
      } else {
        sms_error(result.message || 'Erro ao adicionar cliente');
      }
    } catch (error) {
      sms_error('Erro ao adicionar cliente: ' + error.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
      const result = await Api.AlterCliente({ id: selectedCliente.key, ...values });
      if (result.success) {
        sms_success('Cliente editado com sucesso!');
        setIsEditModalVisible(false);
        editForm.resetFields();
        await fetchClientesAtivos();
      }
      else if (result.message==='Request failed with status code 409')
        {
          sms_error('Não pode editar este cliente');
       }
      else {
        sms_error(result.message || 'Erro ao editar cliente');
      }
    } catch (error) {
      sms_error('Erro ao editar cliente: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const result = await Api.DeleteCliente({ id: clienteToDelete });
      if (result.success) {
        sms_success('Cliente excluído com sucesso!');
        await fetchClientesAtivos();
      } 
      else if (result.message==='Request failed with status code 501')
        {
          sms_error('Não pode excluir este cliente');
       }
      
      else {
        sms_error(result.message || 'Erro ao excluir cliente');
      }
    } catch (error) {
      sms_error('Erro ao excluir cliente');
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  const handleRestore = async (id) => {
    setRestoreLoading(true);
    try {
      const result = await Api.AlterStatusCliente({ id, status: 1 });
      if (result.success) {
        sms_success('Cliente restaurado com sucesso!');
        await fetchClientesAtivos();
        await fetchClientesInativos();
      } else {
        sms_error(result.message || 'Erro ao restaurar cliente');
      }
    } catch {
      sms_error('Erro ao restaurar cliente');
    } finally {
      setRestoreLoading(false);
    }
  };

  const columnsAtivos = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    { title: 'NIF', dataIndex: 'nif', key: 'nif' },
    { title: 'Telefone', dataIndex: 'telefone', key: 'telefone' },
    { title: 'Endereço', dataIndex: 'endereco', key: 'endereco' },
    { title: 'Tipo', dataIndex: 'tipo', key: 'tipo' },
    { title: 'Nacionalidade', dataIndex: 'nacionalidade', key: 'nacionalidade' },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 10 }}>
          <FaEdit
            style={{ cursor: 'pointer', color: '#1890ff' }}
            onClick={() => {
              setSelectedCliente(record);
              editForm.setFieldsValue(record);
              setIsEditModalVisible(true);
            }}
          />
          <FaRegTrashAlt
            style={{ cursor: 'pointer', color: '#ff4d4f' }}
            onClick={() => {
              setClienteToDelete(record.key);
              setIsDeleteModalVisible(true);
            }}
          />
        </div>
      )
    }
  ];

  const columnsInativos = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    { title: 'NIF', dataIndex: 'nif', key: 'nif' },
    { title: 'Telefone', dataIndex: 'telefone', key: 'telefone' },
    { title: 'Endereço', dataIndex: 'endereco', key: 'endereco' },
    { title: 'Tipo', dataIndex: 'tipo', key: 'tipo' },
    { title: 'Nacionalidade', dataIndex: 'nacionalidade', key: 'nacionalidade' },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <Button
          type="link"
          icon={<FaUndo style={{ color: 'green' }} />}
          onClick={() => handleRestore(record.key)}
          loading={restoreLoading}
        >
          Restaurar
        </Button>
      )
    }
  ];

  return (
    <div>
      <ToastContainer />

      <Tabs defaultActiveKey="1">
        <TabPane tab="Clientes Activos" key="1">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.setFieldsValue({ id_nacionalidade: 3, tipo: 'SINGULAR' });
                setIsAddModalVisible(true);
              
              }}
            >
              Adicionar 
            </Button>
          </div>

          <Spin spinning={loading}>
            <Table columns={columnsAtivos} dataSource={clientesAtivos} rowKey="key" />
          </Spin>
        </TabPane>

        <TabPane tab="Clientes Inactivos" key="2">
          <Spin spinning={loading}>
            <Table columns={columnsInativos} dataSource={clientesInativos} rowKey="key" />
          </Spin>
        </TabPane>
      </Tabs>

     
      {/* Modal Adicionar */}
      <Modal
        title="Adicionar Cliente"
        visible={isAddModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsAddModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          {/* Nome + NIF */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="nome"
                label="Nome"
                rules={[{ required: true, message: 'Nome é obrigatório' }]}
              >
                <Input maxLength={150} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nif"
                label="NIF"
                rules={[{ required: true, message: 'NIF é obrigatório' }]}
              >
                <Input maxLength={20} />
              </Form.Item>
            </Col>
          </Row>

          {/* Telefone + Tipo + Nacionalidade */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="telefone"
                label="Telefone"
                rules={[{ required: true, message: 'Telefone é obrigatório' }]}
              >
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="tipo"
                label="Tipo"
                rules={[{ required: true, message: 'Tipo é obrigatório' }]}
              >
                <Select>
                  <Option value="SINGULAR">SINGULAR</Option>
                  <Option value="COLECTIVA">COLECTIVA</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="id_nacionalidade"
                label="Nacionalidade"
                rules={[{ required: true, message: 'Nacionalidade é obrigatória' }]}
              >
                <Select>
                  {nacionalidades.map(nac => (
                    <Option key={nac.id} value={nac.id}>
                      {nac.nacionalidade}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Endereço */}
          <Form.Item
            name="endereco"
            label="Endereço"
            rules={[{ required: true, message: 'Endereço é obrigatório' }]}
          >
            <Input.TextArea rows={2} maxLength={250} />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  form.resetFields();
                  setIsAddModalVisible(false);
                }}
                disabled={addLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={addLoading}>
                {addLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar */}
      <Modal
        title="Editar Cliente"
        visible={isEditModalVisible}
        onCancel={() => {
          editForm.resetFields();
          setIsEditModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          {/* Nome + NIF */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="nome" label="Nome" rules={[{ required: true }]}>
                <Input maxLength={150} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="nif" label="NIF" rules={[{ required: true }]}>
                <Input maxLength={20} />
              </Form.Item>
            </Col>
          </Row>

          {/* Telefone + Tipo + Nacionalidade */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="telefone" label="Telefone" rules={[{ required: true }]}>
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
                <Select>
                  <Option value="SINGULAR">SINGULAR</Option>
                  <Option value="COLECTIVA">COLECTIVA</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="id_nacionalidade"
                label="Nacionalidade"
                rules={[{ required: true }]}
              >
                <Select>
                  {nacionalidades.map(nac => (
                    <Option key={nac.id} value={nac.id}>
                      {nac.nacionalidade}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Endereço */}
          <Form.Item name="endereco" label="Endereço" rules={[{ required: true }]}>
            <Input.TextArea rows={2} maxLength={250} />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  editForm.resetFields();
                  setIsEditModalVisible(false);
                }}
                disabled={editLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                {editLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Excluir */}
      <Modal
        title="Confirmar Exclusão"
        visible={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Sim"
        cancelText="Não"
        confirmLoading={deleteLoading}
      >
        <p>Tem certeza que deseja excluir este cliente?</p>
      </Modal>
    </div>
  );
};

export default Cliente;
